import { Injectable } from '@angular/core';
import { Observable, map, of, tap } from 'rxjs';
import { ApiClient } from './api-client.service';
import { AuthTokenService } from './auth-token.service';

interface PermissionCheckResponse {
  isAllowed: boolean;
}

interface BulkPermissionCheckResponse {
  results: Record<string, boolean>;
}

@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly cache = new Map<string, boolean>();
  private readonly cacheTtlMs = 60 * 1000;
  private loadedFromStorage = false;
  private cacheTimestamp = 0;

  constructor(
    private readonly api: ApiClient,
    private readonly tokenStore: AuthTokenService
  ) {}

  hasPermission(permissionCode: string): Observable<boolean> {
    this.ensureCacheLoaded();
    this.evictExpiredCache();
    const cacheKey = this.buildCacheKey(permissionCode);
    const cached = this.cache.get(cacheKey);
    if (cached !== undefined) {
      return of(cached);
    }

    const claims = this.getClaims();
    if (!claims?.accountId) {
      this.cache.set(cacheKey, false);
      return of(false);
    }

    return this.api
      .post<PermissionCheckResponse>('/api/auth/check-permission', {
        accountId: claims.accountId,
        permissionCode,
        tenantId: claims.tenantId,
        branchId: claims.branchId
      })
      .pipe(
        tap((result) => {
          this.cache.set(cacheKey, result.isAllowed);
          this.persistCache();
        }),
        map((result) => result.isAllowed)
      );
  }

  hasPermissions(permissionCodes: string[]): Observable<Record<string, boolean>> {
    this.ensureCacheLoaded();
    this.evictExpiredCache();
    const uniqueCodes = Array.from(new Set(permissionCodes));
    const claims = this.getClaims();
    if (!claims?.accountId) {
      return of(Object.fromEntries(uniqueCodes.map((code) => [code, false])));
    }

    const missing = uniqueCodes.filter((code) => !this.cache.has(this.buildCacheKey(code)));
    if (missing.length === 0) {
      return of(this.toResultMap(uniqueCodes));
    }

    return this.api
      .post<BulkPermissionCheckResponse>('/api/auth/check-permissions', {
        accountId: claims.accountId,
        permissionCodes: missing,
        tenantId: claims.tenantId,
        branchId: claims.branchId
      })
      .pipe(
        tap((response) => {
          Object.entries(response.results ?? {}).forEach(([code, allowed]) => {
            this.cache.set(this.buildCacheKey(code), allowed);
          });
          this.persistCache();
        }),
        map(() => this.toResultMap(uniqueCodes))
      );
  }

  clear(): void {
    this.cache.clear();
    this.cacheTimestamp = 0;
    this.loadedFromStorage = false;
    this.removePersistedCache();
  }

  private buildCacheKey(permissionCode: string): string {
    const claims = this.getClaims();
    return [permissionCode, claims?.accountId ?? '-', claims?.tenantId ?? '-', claims?.branchId ?? '-'].join('|');
  }

  private toResultMap(permissionCodes: string[]): Record<string, boolean> {
    return Object.fromEntries(
      permissionCodes.map((code) => [code, this.cache.get(this.buildCacheKey(code)) ?? false])
    );
  }

  private getClaims(): { accountId?: string; tenantId?: string; branchId?: string } | null {
    const token = this.tokenStore.getAccessToken();
    if (!token) {
      return null;
    }
    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }
    try {
      const payload = JSON.parse(atob(this.padBase64(parts[1].replace(/-/g, '+').replace(/_/g, '/'))));
      return {
        accountId: payload.sub,
        tenantId: payload.tid,
        branchId: payload.bid
      };
    } catch {
      return null;
    }
  }

  private padBase64(value: string): string {
    const pad = value.length % 4;
    if (pad === 0) {
      return value;
    }
    return value.padEnd(value.length + (4 - pad), '=');
  }

  private ensureCacheLoaded(): void {
    if (this.loadedFromStorage) {
      return;
    }
    this.loadedFromStorage = true;
    const key = this.getStorageKey();
    if (!key) {
      return;
    }
    try {
      const raw = localStorage.getItem(key);
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw) as {
        timestamp?: number;
        entries?: Array<[string, boolean]>;
      };
      if (!parsed?.timestamp || !Array.isArray(parsed.entries)) {
        return;
      }
      if (Date.now() - parsed.timestamp > this.cacheTtlMs) {
        localStorage.removeItem(key);
        return;
      }
      parsed.entries.forEach(([k, v]) => {
        this.cache.set(k, Boolean(v));
      });
      this.cacheTimestamp = parsed.timestamp;
    } catch {
      // Ignore malformed cached payloads.
    }
  }

  private persistCache(): void {
    const key = this.getStorageKey();
    if (!key) {
      return;
    }
    try {
      const timestamp = Date.now();
      this.cacheTimestamp = timestamp;
      localStorage.setItem(
        key,
        JSON.stringify({
          timestamp,
          entries: Array.from(this.cache.entries())
        })
      );
    } catch {
      // Ignore storage write failures.
    }
  }

  private removePersistedCache(): void {
    const key = this.getStorageKey();
    if (!key) {
      return;
    }
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignore storage remove failures.
    }
  }

  private evictExpiredCache(): void {
    if (!this.cacheTimestamp) {
      return;
    }
    if (Date.now() - this.cacheTimestamp <= this.cacheTtlMs) {
      return;
    }
    this.cache.clear();
    this.cacheTimestamp = 0;
    this.removePersistedCache();
  }

  private getStorageKey(): string | null {
    const claims = this.getClaims();
    if (!claims?.accountId) {
      return null;
    }
    return [
      'perm-cache',
      claims.accountId ?? '-',
      claims.tenantId ?? '-',
      claims.branchId ?? '-'
    ].join('|');
  }
}
